import { useEffect, useRef, useState } from 'react';
import type { CloudToDashboardMessage } from '@timer-manager/shared';

export type WsStatus = 'connecting' | 'open' | 'closed';

/**
 * Force-reconnect if no inbound message for this long. Second line of defence
 * against zombie connections: the server already pings at 20s cadence, so
 * silence past 45s means either the server itself is stuck or the browser is
 * behind a middle-box that strips WS control frames. Either way, reconnect.
 */
const INBOUND_IDLE_TIMEOUT_MS = 45_000;

interface Options {
  url: string;
  onMessage: (msg: CloudToDashboardMessage) => void;
}

/**
 * Maintains a WebSocket connection to the cloud dashboard endpoint with
 * exponential backoff reconnection. Parses messages as CloudToDashboardMessage
 * and forwards them to the caller via `onMessage`.
 *
 * Exposes a lightweight `status` for "Reconnecting..." UI states.
 */
export function useWebSocket({ url, onMessage }: Options): WsStatus {
  const [status, setStatus] = useState<WsStatus>('connecting');
  const onMessageRef = useRef(onMessage);

  // Keep a live ref so the effect below doesn't re-run every render.
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let cancelled = false;

    const clearIdleTimer = (): void => {
      if (idleTimer) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }
    };

    const resetIdleTimer = (): void => {
      clearIdleTimer();
      idleTimer = setTimeout(() => {
        // Connection looks open but nothing is arriving — assume zombie and
        // force a reconnect via the existing close → reconnect path.
        try {
          ws?.close();
        } catch {
          // ignore
        }
      }, INBOUND_IDLE_TIMEOUT_MS);
    };

    const connect = (): void => {
      if (cancelled) return;
      setStatus('connecting');

      try {
        ws = new WebSocket(url);
      } catch (err) {
        console.error('[ws] failed to create', err);
        scheduleReconnect();
        return;
      }

      ws.addEventListener('open', () => {
        attempt = 0;
        setStatus('open');
        resetIdleTimer();
      });

      ws.addEventListener('message', (ev: MessageEvent<string>) => {
        resetIdleTimer();
        try {
          const parsed = JSON.parse(ev.data) as CloudToDashboardMessage;
          onMessageRef.current(parsed);
        } catch (err) {
          console.warn('[ws] bad message', err);
        }
      });

      ws.addEventListener('close', () => {
        clearIdleTimer();
        setStatus('closed');
        scheduleReconnect();
      });

      ws.addEventListener('error', () => {
        // 'close' will follow; no extra action here.
      });
    };

    const scheduleReconnect = (): void => {
      if (cancelled) return;
      if (reconnectTimer) return;
      // Exponential backoff: 500, 1000, 2000, 4000 ... capped at 10s.
      const delay = Math.min(10_000, 500 * 2 ** attempt);
      attempt += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delay);
    };

    connect();

    return (): void => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      clearIdleTimer();
      if (ws) {
        try {
          ws.close();
        } catch {
          // ignore
        }
      }
    };
  }, [url]);

  return status;
}
